/**
 * pages/Dashboard.jsx — Rently.ma Fleet OS Business Dashboard
 *
 * The "morning check" — first screen the agency owner sees every day.
 * Answers "how's my business doing?" in under 5 seconds.
 *
 * Data: fleet.summary(), rentals.list(), narsa.stats(), dashboard.status()
 * Each section loads independently — one failure doesn't block the rest.
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fleet as fleetApi,
  rentals as rentalsApi,
  narsa as narsaApi,
  dashboard as dashApi,
  dgsn as dgsnApi,
  caisse as caisseApi,
} from '../services/api';
import { formatShortDate, formatLongDate, formatNumber } from '../utils/formatters';
import {
  StatCard,
  Badge,
  Card,
  EmptyState,
  SkeletonCard,
  SkeletonBlock,
  NotificationBadge,
} from '../components/ui';

// ── Lazy-loaded map (below the fold) ─────────────────────────────────
const MapSection = lazy(() => import('./MapSection'));

// ── Map loading fallback ─────────────────────────────────────────────
function MapFallback() {
  const { t } = useTranslation();
  return (
    <div style={{
      height: 280, background: 'var(--elevated)', borderRadius: 'var(--radius)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 'var(--text-body)', color: 'var(--text-muted)' }}>
        {t('dashboard.mapLoading')}
      </span>
    </div>
  );
}

// ── Daily billing breakdown from active rentals ─────────────────────

// ── Section error banner ─────────────────────────────────────────────
function SectionError({ message }) {
  return (
    <div style={{
      fontSize: 'var(--text-caption)', color: 'var(--red)',
      padding: 'var(--space-md)', background: 'var(--status-alert-bg)',
      borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-sm)',
    }}>
      {message}
    </div>
  );
}

// ── Daily Billing Panel — honest display of active rental rates ──────
function BillingPanel({ rentals = [], dailyRevenue = 0 }) {
  const { t } = useTranslation();

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
          {t('dashboard.dailyBillingRate')}
        </div>
        <div style={{
          fontSize: 'var(--text-caption)', color: 'var(--text-muted)',
          marginTop: 'var(--space-xs)',
        }}>
          {rentals.length > 0
            ? t('dashboard.basedOnActive', { count: rentals.length })
            : t('dashboard.noRevenueData')}
        </div>
      </div>

      <div style={{
        fontSize: 'var(--text-display)', fontWeight: 700,
        color: 'var(--gold)', fontFamily: 'var(--font-mono)',
        letterSpacing: '-0.02em',
        marginBottom: 'var(--space-lg)',
        direction: 'ltr', display: 'inline-block',
      }}>
        {formatNumber(dailyRevenue || 0)}
        <span style={{ fontSize: 'var(--text-body)', fontWeight: 500, marginInlineStart: 8 }}>{t('dashboard.madPerDay')}</span>
      </div>

      {rentals.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {rentals.slice(0, 5).map((r, i) => (
            <div key={r.id || i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: 'var(--space-sm) 0',
              borderBottom: i < Math.min(rentals.length, 5) - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                {r.plate_number || r.vehicle_plate || '--'}
              </span>
              <span style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)', direction: 'ltr' }}>
                {formatNumber(r.daily_price_mad || 0)} MAD
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-xl) 0' }}>
          {t('dashboard.noActiveRentals')}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────
export default function Dashboard({ onNavigate }) {
  const { t } = useTranslation();
  // Independent state per section
  const [fleetState, setFleetState] = useState({ loading: true, error: null, data: null });
  const [rentalsState, setRentalsState] = useState({ loading: true, error: null, data: null });
  const [narsaState, setNarsaState] = useState({ loading: true, error: null, data: null });
  const [systemState, setSystemState] = useState({ loading: true, error: null, data: null });
  const [dgsnState, setDgsnState] = useState({ loading: true, error: null, data: null });
  const [caisseState, setCaisseState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    const ac = new AbortController();
    const s = ac.signal;

    fleetApi.summary()
      .then(d => { if (!s.aborted) setFleetState({ loading: false, error: null, data: d }); })
      .catch(e => { if (!s.aborted) { setFleetState({ loading: false, error: e.message, data: null }); } });

    rentalsApi.list(20)
      .then(d => {
        if (s.aborted) return;
        const active = (d?.rentals || []).filter(r =>
          ['active', 'late', 'reserved'].includes(r.status)
        );
        setRentalsState({ loading: false, error: null, data: active });
      })
      .catch(e => { if (!s.aborted) { setRentalsState({ loading: false, error: e.message, data: null }); } });

    narsaApi.stats()
      .then(d => { if (!s.aborted) setNarsaState({ loading: false, error: null, data: d }); })
      .catch(e => { if (!s.aborted) { setNarsaState({ loading: false, error: e.message, data: null }); } });

    dashApi.status()
      .then(d => { if (!s.aborted) setSystemState({ loading: false, error: null, data: d }); })
      .catch(e => { if (!s.aborted) { setSystemState({ loading: false, error: e.message, data: null }); } });

    dgsnApi.pending()
      .then(d => { if (!s.aborted) setDgsnState({ loading: false, error: null, data: d }); })
      .catch(() => { if (!s.aborted) setDgsnState({ loading: false, error: null, data: null }); });

    caisseApi.today()
      .then(d => { if (!s.aborted) setCaisseState({ loading: false, error: null, data: d }); })
      .catch(() => { if (!s.aborted) setCaisseState({ loading: false, error: null, data: null }); });

    // Refresh every 30s
    const iv = setInterval(() => {
      fleetApi.summary()
        .then(d => setFleetState({ loading: false, error: null, data: d }))
        .catch(() => {});
      rentalsApi.list(20)
        .then(d => {
          const active = (d?.rentals || []).filter(r =>
            ['active', 'late', 'reserved'].includes(r.status)
          );
          setRentalsState({ loading: false, error: null, data: active });
        })
        .catch(() => {});
      narsaApi.stats()
        .then(d => setNarsaState({ loading: false, error: null, data: d }))
        .catch(() => {});
    }, 30000);

    return () => { ac.abort(); clearInterval(iv); };
  }, []);

  const fs = fleetState.data;
  const ns = narsaState.data;
  const rentals = rentalsState.data;
  const connected = !systemState.error;

  const today = formatLongDate(new Date());

  // Revenue today = sum of daily_price_mad from all active rentals
  const dailyRevenue = rentals
    ? rentals.reduce((sum, r) => sum + (r.daily_price_mad || 0), 0)
    : 0;

  // Late rental count for status strip
  const lateCount = rentals ? rentals.filter(r => r.status === 'late').length : 0;

  // DGSN pending count — API returns { pending_days: [...] } or similar
  const dgsnRaw = dgsnState.data;
  const dgsnPendingCount = dgsnRaw
    ? (Array.isArray(dgsnRaw.pending_days) ? dgsnRaw.pending_days.length
      : Array.isArray(dgsnRaw) ? dgsnRaw.length
      : null)
    : null;

  // Build alerts from real data when available, otherwise mocks
  const alerts = [];
  if (ns && ns.open_count > 0) {
    alerts.push({
      bgVar: 'var(--status-alert-bg)', colorVar: 'var(--status-alert)',
      iconColor: 'var(--red)',
      text: t('dashboard.alertNarsaFines', { count: ns.open_count, amount: formatNumber(ns.total_exposure_mad || 0) }),
      plate: '', time: ns.urgent_count > 0 ? t('dashboard.alertUrgent') : t('dashboard.alertPending'),
      navigateTo: 'narsa-page',
    });
  }
  if (fs && fs.overdue_service > 0) {
    alerts.push({
      bgVar: 'var(--status-maintenance-bg)', colorVar: 'var(--status-maintenance)',
      iconColor: 'var(--orange)', text: t('dashboard.alertServiceDue', { count: fs.overdue_service }),
      plate: '', time: t('dashboard.alertActionRequired'),
      navigateTo: 'fleet-mgr',
    });
  }
  if (fs && fs.expiring_soon > 0) {
    alerts.push({
      bgVar: 'var(--status-maintenance-bg)', colorVar: 'var(--status-maintenance)',
      iconColor: 'var(--gold)', text: t('dashboard.alertDocsExpiring', { count: fs.expiring_soon }),
      plate: '', time: t('dashboard.alertVerify'),
      navigateTo: 'fleet-mgr',
    });
  }
  const displayAlerts = alerts;

  return (
    <div>
      {/* ── Top Bar ────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 'var(--space-xl)',
        animation: 'fadeIn 300ms var(--ease-smooth) both',
      }}>
        <div>
          <h1 style={{
            fontSize: 'var(--text-display)', fontWeight: 700,
            color: 'var(--text)', letterSpacing: '-0.02em',
          }}>
            {t('dashboard.title')}
          </h1>
          <p style={{
            fontSize: 'var(--text-body)', color: 'var(--text-secondary)',
            marginTop: 'var(--space-xs)',
          }}>
            {today}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)' }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: connected ? 'var(--green)' : 'var(--orange)',
            animation: connected ? 'pulse 2s infinite' : 'none',
          }} />
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            {connected ? t('common.connected') : t('common.offline')}
          </span>
        </div>
      </div>

      {/* ── Operational Status Strip ───────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 'var(--space-xl)',
        padding: '9px 16px',
        background: 'var(--elevated)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        animation: 'fadeIn 300ms var(--ease-smooth) 60ms both',
      }}>
        {/* DGSN Today */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
            background: dgsnState.loading ? 'var(--text-muted)'
              : dgsnPendingCount === 0 ? 'var(--green)'
              : dgsnPendingCount === null ? 'var(--text-muted)'
              : 'var(--orange)',
          }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>DGSN</span>
          <span style={{
            fontSize: 11,
            color: dgsnState.loading ? 'var(--text-muted)'
              : dgsnPendingCount === 0 ? 'var(--green)'
              : dgsnPendingCount === null ? 'var(--text-muted)'
              : 'var(--orange)',
          }}>
            {dgsnState.loading ? '…'
              : dgsnPendingCount === 0 ? t('dgsn.statusSubmitted')
              : dgsnPendingCount === null ? '—'
              : t('dashboard.dgsnPending', { count: dgsnPendingCount })}
          </span>
        </div>

        <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />

        {/* Late Rentals */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
            background: rentalsState.loading ? 'var(--text-muted)' : lateCount > 0 ? 'var(--red)' : 'var(--green)',
          }} />
          <span style={{
            fontSize: 11,
            color: rentalsState.loading ? 'var(--text-muted)' : lateCount > 0 ? 'var(--red)' : 'var(--text-muted)',
            fontWeight: lateCount > 0 ? 600 : 400,
          }}>
            {rentalsState.loading ? '…' : lateCount > 0 ? t('dashboard.rentalsLate', { count: lateCount }) : t('dashboard.rentalsOkay')}
          </span>
        </div>

        <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />

        {/* NARSA */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
            background: narsaState.loading ? 'var(--text-muted)'
              : ns?.urgent_count > 0 ? 'var(--red)'
              : ns?.open_count > 0 ? 'var(--gold)'
              : 'var(--green)',
          }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>NARSA</span>
          <span style={{
            fontSize: 11,
            color: narsaState.loading ? 'var(--text-muted)'
              : ns?.urgent_count > 0 ? 'var(--red)'
              : ns?.open_count > 0 ? 'var(--gold)'
              : 'var(--text-muted)',
            fontWeight: ns?.urgent_count > 0 ? 600 : 400,
          }}>
            {narsaState.loading ? '…'
              : ns?.urgent_count > 0 ? t('narsa.urgentCount', { count: ns.urgent_count })
              : ns?.open_count > 0 ? t('narsa.openCount', { count: ns.open_count })
              : t('common.statusOk')}
          </span>
        </div>

        {/* Fleet issues — show only when relevant */}
        {!fleetState.loading && (fs?.overdue_service > 0 || fs?.expiring_soon > 0) && (
          <>
            <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block', background: 'var(--orange)' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('nav.fleet')}</span>
              <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>
                {t('dashboard.fleetIssueCount', { count: (fs.overdue_service || 0) + (fs.expiring_soon || 0) })}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Row 1: Stat Cards ──────────────────────────────────── */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        {fleetState.loading ? (
          <>
            <SkeletonCard height={100} />
            <SkeletonCard height={100} />
            <SkeletonCard height={100} />
            <SkeletonCard height={100} />
          </>
        ) : (
          <>
            <StatCard
              label={t('dashboard.fleet')}
              value={fs?.total ?? '--'}
              trend={`${fs?.available ?? 0} ${t('dashboard.available')}`}
              trendUp={true}
              index={0}
            />
            <StatCard
              label={t('dashboard.activeRentals')}
              value={rentals?.length ?? (rentalsState.loading ? '--' : 0)}
              trend={`${rentals?.length ?? 0} ${t('dashboard.contracts')}`}
              trendUp={true}
              index={1}
            />
            <StatCard
              label={t('dashboard.revenue_today')}
              value={caisseState.loading ? '--' : `${formatNumber(caisseState.data?.total_mad || 0)} MAD`}
              trend={
                caisseState.data?.count
                  ? t('dashboard.paymentsCollected', { count: caisseState.data.count })
                  : rentals?.length
                  ? t('dashboard.activeLocationsRevenue', { count: rentals.length, amount: formatNumber(dailyRevenue || 0) })
                  : ''
              }
              trendUp={(caisseState.data?.total_mad || 0) > 0}
              index={2}
            />
            <StatCard
              label={t('narsa.title')}
              value={ns?.open_count ?? (narsaState.loading ? '--' : 0)}
              trend={ns?.total_exposure_mad ? `${formatNumber(ns.total_exposure_mad)} MAD` : '0 MAD'}
              trendUp={false}
              index={3}
            />
          </>
        )}
      </div>

      {/* ── Row 2: Revenue Chart + Map ─────────────────────────── */}
      <div className="grid-2col" style={{ marginBottom: 'var(--space-xl)' }}>
        <div className="lazy-section contain-panel">
          <Card>
            <BillingPanel rentals={rentals || []} dailyRevenue={dailyRevenue} />
          </Card>
        </div>

        <div className="lazy-section contain-panel">
          <Card>
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
                {t('dashboard.region')}
              </div>
              <div style={{
                fontSize: 'var(--text-caption)', color: 'var(--text-muted)',
                marginTop: 'var(--space-xs)',
              }}>
                {t('dashboard.regionCities')}
              </div>
            </div>
            <Suspense fallback={<MapFallback />}>
              <MapSection />
            </Suspense>
            {/* Map legend */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 16, marginTop: 'var(--space-md)', flexWrap: 'wrap',
            }}>
              {[
                { color: '#4ADE80', labelKey: 'fleet.available' },
                { color: '#5B9BD5', labelKey: 'fleet.rented' },
                { color: '#FBBF24', labelKey: 'fleet.maintenance' },
                { color: '#9090A0', labelKey: 'fleet.reserved' },
              ].map(item => (
                <div key={item.labelKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: item.color, display: 'inline-block', flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {t(item.labelKey)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* ── Row 3: Active Rentals + Urgent Actions ─────────────── */}
      <div className="grid-2col">
        {/* Active Rentals */}
        <div className="contain-panel">
          <Card>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)',
            }}>
              <span style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
                {t('dashboard.activeRentals')}
              </span>
              {rentals && rentals.length > 0 && (
                <Badge label={String(rentals.length)} variant="info" />
              )}
            </div>

            {rentalsState.loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
                    <SkeletonBlock width="40%" height={14} />
                    <SkeletonBlock width="25%" height={12} />
                    <SkeletonBlock width="20%" height={12} />
                    <SkeletonBlock width={60} height={22} radius={12} />
                  </div>
                ))}
              </div>
            ) : rentalsState.error ? (
              <SectionError message={rentalsState.error} />
            ) : !rentals || rentals.length === 0 ? (
              <EmptyState
                icon={null}
                title={t('dashboard.noActiveRentals')}
                description={t('dashboard.allAvailable')}
                ctaLabel={t('rentals.newRental')}
                onCta={() => onNavigate && onNavigate('rentals-page')}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rentals.slice(0, 6).map((r, i) => (
                  <div
                    key={r.id || i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                      padding: '10px var(--space-sm)',
                      borderBottom: i < Math.min(rentals.length, 6) - 1 ? '1px solid var(--border)' : 'none',
                      animation: `fadeIn 300ms var(--ease-smooth) ${i * 30}ms both`,
                      borderRadius: 'var(--radius-sm)',
                      transition: 'background var(--duration-fast) var(--ease-smooth)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--raised)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0, direction: 'ltr' }}>
                      <div style={{
                        fontWeight: 600, color: 'var(--text)', fontSize: 13,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {r.client_name || t('dashboard.clientUnknown')}
                      </div>
                      <div style={{
                        fontSize: 11, color: 'var(--text-muted)', marginTop: 2,
                        display: 'flex', alignItems: 'center', gap: 4, direction: 'ltr',
                      }}>
                        <span>{r.make || ''}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-secondary)' }}>
                          {r.plate_number || r.vehicle_plate || '--'}
                        </span>
                      </div>
                    </div>
                    <div style={{
                      fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', direction: 'ltr',
                    }}>
                      {formatShortDate(r.start_date)} → {formatShortDate(r.end_date)}
                    </div>
                    <Badge
                      label={r.status === 'active' ? t('dashboard.statusActive') : r.status === 'late' ? t('dashboard.statusLate') : r.status}
                      variant={r.status === 'active' ? 'info' : r.status === 'late' ? 'danger' : 'neutral'}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Urgent Actions */}
        <div className="contain-panel">
          <Card>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)',
            }}>
              <span style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
                {t('dashboard.urgentActions')}
              </span>
              {displayAlerts.length > 0 && <NotificationBadge count={displayAlerts.length} />}
            </div>

            {fleetState.loading && narsaState.loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <SkeletonBlock height={56} />
                <SkeletonBlock height={56} />
                <SkeletonBlock height={56} />
              </div>
            ) : displayAlerts.length === 0 ? (
              <div style={{
                padding: 'var(--space-xl)', textAlign: 'center',
                color: 'var(--green)', fontSize: 'var(--text-body)',
              }}>
                {t('dashboard.allClear')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {displayAlerts.map((alert, i) => (
                  <div
                    key={i}
                    onClick={() => onNavigate && alert.navigateTo && onNavigate(alert.navigateTo)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
                      padding: 'var(--space-md)',
                      borderRadius: 8,
                      background: 'transparent',
                      cursor: 'pointer',
                      animation: `slideInRight 300ms var(--ease-smooth) ${i * 40}ms both`,
                      transition: 'background var(--duration-fast) var(--ease-smooth)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'var(--raised)';
                      const timeEl = e.currentTarget.querySelector('[data-alert-time]');
                      if (timeEl) timeEl.style.color = 'var(--gold)';
                      const chevron = e.currentTarget.querySelector('[data-alert-chevron]');
                      if (chevron) chevron.style.opacity = '1';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      const timeEl = e.currentTarget.querySelector('[data-alert-time]');
                      if (timeEl) timeEl.style.color = 'var(--text-muted)';
                      const chevron = e.currentTarget.querySelector('[data-alert-chevron]');
                      if (chevron) chevron.style.opacity = '0';
                    }}
                  >
                    <span style={{
                      width: 32, height: 32, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      background: alert.bgVar,
                    }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: alert.iconColor || 'var(--text-muted)',
                        display: 'block', flexShrink: 0,
                      }} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                        {alert.text}
                      </div>
                      {alert.plate && (
                        <span style={{
                          fontSize: 11, color: 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono)', fontWeight: 600,
                        }}>
                          {alert.plate}
                        </span>
                      )}
                    </div>
                    <span
                      data-alert-time
                      style={{
                        fontSize: 'var(--text-caption)', color: 'var(--text-muted)',
                        whiteSpace: 'nowrap', flexShrink: 0,
                        transition: 'color var(--duration-fast) var(--ease-smooth)',
                      }}
                    >
                      {alert.time}
                    </span>
                    <span
                      data-alert-chevron
                      style={{
                        fontSize: 'var(--text-body)', color: 'var(--gold)',
                        opacity: 0, flexShrink: 0,
                        transition: 'opacity var(--duration-fast) var(--ease-smooth)',
                        marginInlineStart: 'var(--space-xs)',
                      }}
                    >
                      →
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
